// Mobile Enhancements Controller
// Provides enhanced mobile interactions and functionality

import ApplicationController from "./application_controller"

export default class extends ApplicationController {
  static targets = ["card", "action", "filter", "search", "modal"]
  static values = { 
    animationDuration: { type: Number, default: 300 },
    touchThreshold: { type: Number, default: 10 }
  }

  connect() {
    this.setupMobileEnhancements()
    this.setupTouchInteractions()
    this.setupSwipeGestures()
    this.setupMobileOptimizations()
  }

  disconnect() {
    this.removeEventListeners()
  }

  // ========================================
  // MOBILE ENHANCEMENT SETUP
  // ========================================

  setupMobileEnhancements() {
    // Add mobile-specific classes
    this.element.classList.add('mobile-enhanced')
    
    // Setup mobile-specific event listeners
    this.setupMobileEventListeners()
    
    // Initialize mobile animations
    this.initializeMobileAnimations()
    
    // Setup mobile-specific optimizations
    this.setupMobileOptimizations()
  }

  setupMobileEventListeners() {
    // Touch events for better mobile interaction
    this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true })
    this.element.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: true })
    this.element.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true })
    
    // Orientation change handling
    window.addEventListener('orientationchange', this.handleOrientationChange.bind(this))
    
    // Resize handling for responsive adjustments
    window.addEventListener('resize', this.handleResize.bind(this))
  }

  // ========================================
  // TOUCH INTERACTIONS
  // ========================================

  setupTouchInteractions() {
    // Enhanced touch feedback for interactive elements
    this.element.addEventListener('touchstart', this.addTouchFeedback.bind(this))
    this.element.addEventListener('touchend', this.removeTouchFeedback.bind(this))
    this.element.addEventListener('touchcancel', this.removeTouchFeedback.bind(this))
  }

  addTouchFeedback(event) {
    const target = event.target.closest('[data-mobile-enhanced]')
    if (target) {
      target.classList.add('touch-active')
    }
  }

  removeTouchFeedback(event) {
    const target = event.target.closest('[data-mobile-enhanced]')
    if (target) {
      target.classList.remove('touch-active')
    }
  }

  // ========================================
  // SWIPE GESTURES
  // ========================================

  setupSwipeGestures() {
    this.touchStartX = 0
    this.touchStartY = 0
    this.touchEndX = 0
    this.touchEndY = 0
  }

  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX
    this.touchStartY = event.touches[0].clientY
  }

  handleTouchMove(event) {
    // Prevent default scrolling during horizontal swipes
    const touchMoveX = event.touches[0].clientX
    const touchMoveY = event.touches[0].clientY
    const deltaX = Math.abs(touchMoveX - this.touchStartX)
    const deltaY = Math.abs(touchMoveY - this.touchStartY)
    
    if (deltaX > deltaY && deltaX > this.touchThresholdValue) {
      event.preventDefault()
    }
  }

  handleTouchEnd(event) {
    this.touchEndX = event.changedTouches[0].clientX
    this.touchEndY = event.changedTouches[0].clientY
    
    this.handleSwipeGesture()
  }

  handleSwipeGesture() {
    const deltaX = this.touchEndX - this.touchStartX
    const deltaY = this.touchEndY - this.touchStartY
    const minSwipeDistance = 50
    
    if (Math.abs(deltaX) > minSwipeDistance && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX > 0) {
        this.handleSwipeRight()
      } else {
        this.handleSwipeLeft()
      }
    } else if (Math.abs(deltaY) > minSwipeDistance && Math.abs(deltaY) > Math.abs(deltaX)) {
      if (deltaY > 0) {
        this.handleSwipeDown()
      } else {
        this.handleSwipeUp()
      }
    }
  }

  handleSwipeLeft() {
    this.dispatch('swipeLeft', { detail: { direction: 'left' } })
  }

  handleSwipeRight() {
    this.dispatch('swipeRight', { detail: { direction: 'right' } })
  }

  handleSwipeUp() {
    this.dispatch('swipeUp', { detail: { direction: 'up' } })
  }

  handleSwipeDown() {
    this.dispatch('swipeDown', { detail: { direction: 'down' } })
  }

  // ========================================
  // MOBILE ANIMATIONS
  // ========================================

  initializeMobileAnimations() {
    // Setup intersection observer for scroll animations
    this.setupScrollAnimations()
    
    // Setup card animations
    this.setupCardAnimations()
    
    // Setup modal animations
    this.setupModalAnimations()
  }

  setupScrollAnimations() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      )
      
      // Observe all elements with fade-in-mobile class
      this.element.querySelectorAll('.fade-in-mobile').forEach(el => {
        this.observer.observe(el)
      })
    }
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-in')
        this.observer.unobserve(entry.target)
      }
    })
  }

  setupCardAnimations() {
    // Add hover effects for mobile cards
    this.element.querySelectorAll('.mobile-data-card, .mobile-quote-card').forEach(card => {
      card.addEventListener('touchstart', this.animateCardTouch.bind(this))
    })
  }

  animateCardTouch(event) {
    const card = event.currentTarget
    card.classList.add('touch-active')
    
    setTimeout(() => {
      card.classList.remove('touch-active')
    }, 150)
  }

  // Toggle card details (accordion style)
  toggleDetails(event) {
    const header = event.currentTarget
    const details = header.nextElementSibling
    
    if (!details || !details.classList.contains('collapse')) {
      return
    }
    
    const isExpanded = details.classList.contains('show')
    const toggleButton = header.querySelector('.collapse-toggle')
    
    if (isExpanded) {
      details.classList.remove('show')
      details.setAttribute('aria-expanded', 'false')
      if (toggleButton) {
        toggleButton.setAttribute('aria-expanded', 'false')
      }
    } else {
      details.classList.add('show')
      details.setAttribute('aria-expanded', 'true')
      if (toggleButton) {
        toggleButton.setAttribute('aria-expanded', 'true')
      }
    }
    
    this.dispatch('detailsToggled', {
      detail: {
        expanded: !isExpanded,
        cardId: header.closest('.quote-bundle-item')?.dataset.id || 'unknown'
      }
    })
  }

  setupModalAnimations() {
    // Enhanced modal animations for mobile
    this.element.addEventListener('show.bs.modal', this.handleModalShow.bind(this))
    this.element.addEventListener('hide.bs.modal', this.handleModalHide.bind(this))
  }

  handleModalShow(event) {
    const modal = event.target
    modal.classList.add('mobile-modal-showing')
  }

  handleModalHide(event) {
    const modal = event.target
    modal.classList.remove('mobile-modal-showing')
  }

  // ========================================
  // MOBILE OPTIMIZATIONS
  // ========================================

  setupMobileOptimizations() {
    // Optimize images for mobile
    this.optimizeImagesForMobile()
    
    // Setup lazy loading
    this.setupLazyLoading()
    
    // Optimize touch targets
    this.optimizeTouchTargets()
    
    // Setup mobile-specific performance optimizations
    this.setupPerformanceOptimizations()
  }

  optimizeImagesForMobile() {
    // Add mobile-specific image optimizations
    this.element.querySelectorAll('img').forEach(img => {
      if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy')
      }
    })
  }

  setupLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target
            if (img.dataset.src) {
              img.src = img.dataset.src
              img.removeAttribute('data-src')
              imageObserver.unobserve(img)
            }
          }
        })
      })
      
      this.element.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img)
      })
    }
  }

  optimizeTouchTargets() {
    // Ensure all interactive elements meet minimum touch target size
    this.element.querySelectorAll('button, a, input, select, textarea').forEach(element => {
      const rect = element.getBoundingClientRect()
      if (rect.width < 44 || rect.height < 44) {
        element.classList.add('touch-target-small')
      }
    })
  }

  setupPerformanceOptimizations() {
    // Debounce scroll events
    this.debouncedScroll = this.debounce(this.handleScroll.bind(this), 16)
    window.addEventListener('scroll', this.debouncedScroll, { passive: true })
    
    // Debounce resize events
    this.debouncedResize = this.debounce(this.handleResize.bind(this), 250)
    window.addEventListener('resize', this.debouncedResize)
  }

  // ========================================
  // EVENT HANDLERS
  // ========================================

  handleOrientationChange() {
    // Handle orientation changes
    setTimeout(() => {
      this.dispatch('orientationChange', { 
        detail: { 
          orientation: window.orientation || screen.orientation?.type || 'unknown' 
        } 
      })
    }, 100)
  }

  handleResize() {
    this.dispatch('resize', { 
      detail: { 
        width: window.innerWidth, 
        height: window.innerHeight 
      } 
    })
  }

  handleScroll() {
    this.dispatch('scroll', { 
      detail: { 
        scrollY: window.scrollY,
        scrollX: window.scrollX 
      } 
    })
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  removeEventListeners() {
    // Clean up event listeners
    if (this.observer) {
      this.observer.disconnect()
    }
    
    window.removeEventListener('orientationchange', this.handleOrientationChange.bind(this))
    window.removeEventListener('resize', this.handleResize.bind(this))
    window.removeEventListener('scroll', this.debouncedScroll)
  }

  // ========================================
  // PUBLIC METHODS
  // ========================================

  // Method to refresh mobile enhancements
  refresh() {
    this.setupMobileEnhancements()
  }

  // Method to add mobile-specific classes
  addMobileClass(className) {
    this.element.classList.add(className)
  }

  // Method to remove mobile-specific classes
  removeMobileClass(className) {
    this.element.classList.remove(className)
  }

  // Method to check if device is mobile
  isMobile() {
    return window.innerWidth < 768
  }

  // Method to check if device is tablet
  isTablet() {
    return window.innerWidth >= 768 && window.innerWidth < 992
  }

  // Method to check if device is desktop
  isDesktop() {
    return window.innerWidth >= 992
  }
}







